import { Box, Button, Icons, Text, Tooltip } from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useMemo } from "react"
import { TWhatsAppThread, WhatsAppThreadXHR } from "../store"
import { Form, withServerErrors } from "@sembark-travel/ui-form"

/**
 * Follow / mute a WhatsApp conversation. Standalone so it can live in the
 * conversation header (trip page) or the inbox context pane.
 */
export function WhatsAppFollowToggle({
	thread,
	onRefresh,
	iconOnly,
}: {
	thread: TWhatsAppThread
	onRefresh: () => Promise<unknown>
	iconOnly?: boolean
}) {
	const xhr = useXHR()
	const actions = useMemo(() => WhatsAppThreadXHR(xhr), [xhr])

	return (
		<Tooltip
			content={
				thread.is_subscribed
					? "You get notified of new messages on this conversation. Click to mute."
					: "You are not notified of new messages on this conversation. Click to follow."
			}
		>
			<Box display="inlineBlock">
				<Form
					initialValues={{
						is_subscribed: thread.is_subscribed,
					}}
					onSubmit={withServerErrors(async () => {
						try {
							if (thread.is_subscribed) {
								await actions.unsubscribe(thread.id)
							} else {
								await actions.subscribe(thread.id)
							}
							await onRefresh()
						} catch (e) {
							showSnackbar(
								(e as Error)?.message || "Unable to update subscription"
							)
						}
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit} noValidate>
							<Button
								inline
								type="submit"
								status={thread.is_subscribed ? undefined : "primary"}
								disabled={submitting}
							>
								<Text fontSize="sm">
									{thread.is_subscribed ? (
										<Icons.Bell size="3" />
									) : (
										<Icons.BellSlash size="3" />
									)}
									{iconOnly
										? null
										: submitting
											? "Updating..."
											: thread.is_subscribed
												? " Following"
												: " Follow"}
								</Text>
							</Button>
						</form>
					)}
				</Form>
			</Box>
		</Tooltip>
	)
}
